import { isNil, forEach, isEmpty, sortBy } from 'lodash';
import moment from 'moment';
import { removeTime } from '@/utils/date';
import { getStatisticType } from '@/utils/statistic';
import { statisticType } from '@/constants/statistic';
import BasicinfoSetApi from '@/api/model/basicinfo-set';

export default {
  /**
   * 라벨 날짜 포맷
   * @type {String}
   */
  LABEL_DATE_FORMAT: 'YY.MM.DD',
  /**
   * 예측 기간 단위 정의 객체
   * @type {Object}
   */
  predictPeriodUnitDefine: {
    MIN: 'min',
    HOUR: 'hour',
    DAY: 'd',
    WEEK: 'w', // BE에선 week 없음.
    MONTH: 'M',
    YEAR: 'y',
  },
  /**
   * 학습 데이터 통계 타입 정의 객체
   * @type {Object}
   */
  learnStatisticsTypeDefine: {
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
    YEAR: 'year',
  },
  /**
   * 최대 예측 기간 일수 (3년 → 1096, moment.add -> diff 카운트 기준으로 계산)
   * @type {Number}
   */
  MAX_PREIDCT_PERIOD_DAYS: 1096,
  /**
   * 통계 데이터 보관 주기 기준정보 객체를 반환한다.
   * @author Ju
   * @return {Object} Promise 객체 { success: true/false, data: 데이터보관주기 정보 객체 }
   */
  getDataStoragePeriodBasicinfo() {
    return new Promise((resolve, reject) => {
      const result = {
        success: false,
        data: {
          // 초기 기준정보로 기본 데이터 설정
          [this.learnStatisticsTypeDefine.DAY]: 7,
          [this.learnStatisticsTypeDefine.WEEK]: 12,
          [this.learnStatisticsTypeDefine.MONTH]: 4,
          [this.learnStatisticsTypeDefine.YEAR]: 2,
        },
      };
      BasicinfoSetApi.getList('SDDESINDEX')
        .then((response) => {
          if (response.data.success) {
            if (!isNil(response.data.data)) {
              forEach(response.data.data, (item) => {
                if (item.basicinfoSubid === 'rscstatrawday') {
                  result.data[this.learnStatisticsTypeDefine.DAY] = item.basicinfoSubvalueInteger;
                } else if (item.basicinfoSubid === 'rscstatrawweek') {
                  result.data[this.learnStatisticsTypeDefine.WEEK] = item.basicinfoSubvalueInteger;
                } else if (item.basicinfoSubid === 'rscstatrawmonth') {
                  result.data[this.learnStatisticsTypeDefine.MONTH] = item.basicinfoSubvalueInteger;
                } else if (item.basicinfoSubid === 'rscstatrawyear') {
                  result.data[this.learnStatisticsTypeDefine.YEAR] = item.basicinfoSubvalueInteger;
                }
              });
            }
            result.success = true;
            resolve(result);
          } else {
            reject(result);
          }
        })
        .catch(() => {
          reject(result);
        });
    });
  },
  /**
   * 예측 기간 단위에 맞는 moment 단위 키 값을 반환한다.
   * @author Ju
   * @param  {[type]} predictPeriodUnit [description]
   * @return {[type]}                   [description]
   */
  getMomentUnitType(predictPeriodUnit) {
    let key;
    if (this.predictPeriodUnitDefine.MIN === predictPeriodUnit) {
      key = 'm';
    } else if (this.predictPeriodUnitDefine.HOUR === predictPeriodUnit) {
      key = 'h';
    } else if (this.predictPeriodUnitDefine.DAY === predictPeriodUnit) {
      key = 'd';
    } else if (this.predictPeriodUnitDefine.WEEK === predictPeriodUnit) {
      key = 'w';
    } else if (this.predictPeriodUnitDefine.MONTH === predictPeriodUnit) {
      key = 'M';
    } else if (this.predictPeriodUnitDefine.YEAR === predictPeriodUnit) {
      key = 'y';
    }
    return key;
  },
  /**
   * 각 단위를 일 기준으로 환산하여 수치를 반환한다.
   * min: 분 / h: 시 / d: 일 / M: 월 / y: 년 (BE 기준)
   * @author Ju
   * @param  {[type]} predictPeriod     [description]
   * @param  {[type]} predictPeriodUnit [description]
   * @return {[type]}                   [description]
   */
  /* eslint-disable no-unused-vars */
  convertToDays(predictPeriod, predictPeriodUnit) {
    const now = new Date();
    const key = this.getMomentUnitType(predictPeriodUnit);
    const days = moment(now)
      .add(predictPeriod, key)
      .diff(now, 'd');
    return days;
  },
  /* eslint-enable no-unused-vars */
  /**
   * 예측 기간에 대한 학습 데이터 통계 타입을 구한다.
   * day: 일 (5분) / week: 주 (30분) / month: 월 (2시간) / year: 년 (1일)
   * @author Ju
   * @param  {Number} predictPeriod    예측 기간
   * @param  {String} predictPeriodUnit 예측 기간 단위
   * @return {String}                  통계 타입
   */
  getLearnStatisticsType(predictPeriod, predictPeriodUnit) {
    const predictDays = this.convertToDays(predictPeriod, predictPeriodUnit);
    let statisticsType = null;
    if (predictDays <= 7) {
      // 5분 통계로 학습
      statisticsType = this.learnStatisticsTypeDefine.DAY;
    } else if (predictDays <= 30) {
      // 30분 통계로 학습
      statisticsType = this.learnStatisticsTypeDefine.WEEK;
    } else if (predictDays <= 365) {
      // 2시간 통계로 학습
      statisticsType = this.learnStatisticsTypeDefine.MONTH;
    } else {
      // 1일 통계
      statisticsType = this.learnStatisticsTypeDefine.YEAR;
    }
    return statisticsType;
  },
  /**
   * 예측 기간에 대한 최대 학습 일 수를 구한다.
   * @author Ju
   * @param  {Number} predictPeriod    예측 기간
   * @param  {String} predictPeriodUnit 예측 기간 단위
   * @return {Number}                  최대 학습 일수
   */
  getMaxLearnDayCount(predictPeriod, predictPeriodUnit) {
    const statisticsType = this.getLearnStatisticsType(predictPeriod, predictPeriodUnit);
    const MAX_LEAWRN_DATA_COUNT = 20000; // 최대 2만개의 데이터를 학습하는 것을 기준으로 계산한다.
    let dayLearnCount = null; // 하루 학습 데이터 건수
    if (statisticsType === this.learnStatisticsTypeDefine.DAY) {
      dayLearnCount = 288;
    } else if (statisticsType === this.learnStatisticsTypeDefine.WEEK) {
      dayLearnCount = 48;
    } else if (statisticsType === this.learnStatisticsTypeDefine.MONTH) {
      dayLearnCount = 12;
    } else {
      // 1일 통계
      dayLearnCount = 1;
    }
    return Math.floor(MAX_LEAWRN_DATA_COUNT / dayLearnCount);
  },
  /**
   * 설정한 예측 기간이 최대 예측 기간을 넘지 않는지 체크한다. 넘으면 false, 아니면 true를 반환한다.
   * @author Ju
   * @param  {Number} predictPeriod              예측 기간
   * @param  {String} predictPeriodUnit           예측 기간 단위
   * @return {Boolean}                   [description]
   */
  isValidPredictPeriod(predictPeriod, predictPeriodUnit) {
    return this.convertToDays(predictPeriod, predictPeriodUnit) <= this.MAX_PREIDCT_PERIOD_DAYS;
  },
  /**
   * 최대 예측 기간을 예측 기간 단위에 맞게 계산하여 반환한다.
   * @author Ju
   * @param  {[type]} predictPeriodUnit [description]
   * @return {[type]}                   [description]
   */
  getMaxPredictPeriodBaseUnit(predictPeriodUnit) {
    const now = new Date();
    const key = this.getMomentUnitType(predictPeriodUnit);
    return moment(now).add(this.MAX_PREIDCT_PERIOD_DAYS, 'd').diff(now, key);
  },
  /**
   * 기준 날짜 기준으로 예측 기간에 맞는 최소 설정 가능 날짜를 반환한다.
   * @author Ju
   * @param  {Object} dataStoragePeriodBasicinfo 통계 데이터 보관 주기 기준정보 객체 (getDataStoragePeriodBasicinfo 반환 객체)
   * @param  {Date} baseDatetime               기준 날짜
   * @param  {Number} predictPeriod              예측 기간
   * @param  {String} predictPeriodUnit           예측 기간 단위
   * @return {Date}                            최소 설정 가능 날짜
   */
  getMinDateByPredictPeriod(dataStoragePeriodBasicinfo, baseDatetime, predictPeriod, predictPeriodUnit) {
    const predictDays = this.convertToDays(predictPeriod, predictPeriodUnit);
    // 예측 기간이 30일 이하인 경우 월간 통계 데이터의 보관 기준으로, 그 이상은 연간 통계 데이터 보관 주기 기준으로 최소 날짜를 구한다.\
    const learnStatisticsType = predictDays <= 30 ? this.learnStatisticsTypeDefine.MONTH : this.learnStatisticsTypeDefine.YEAR;
    const maxLearnDayCount = dataStoragePeriodBasicinfo[learnStatisticsType];
    const key = this.getMomentUnitType(learnStatisticsType === this.learnStatisticsTypeDefine.MONTH ? this.predictPeriodUnitDefine.MONTH : this.predictPeriodUnitDefine.YEAR);
    const minDatetime = moment(baseDatetime)
      .subtract(maxLearnDayCount, key)
      .toDate();
    return minDatetime;
  },
  /**
   * 예측 기간에 대한 권장 학습 일 수를 반환한다.
   * @author Ju
   * @param  {Number} predictPeriod              예측 기간
   * @param  {String} predictPeriodUnit           예측 기간 단위
   * @return {Number}                  권장 학습 일수
   */
  getRecommendLearnDayCount(predictPeriod, predictPeriodUnit) {
    const predictDays = this.convertToDays(predictPeriod, predictPeriodUnit);
    return predictDays * 6;
  },
  /**
   * 학습 데이터 배열에서 설정한 기간 범위와 기간이 겹치는 학습 데이터의 인덱스를 찾아 반환한다.
   * @author Ju
   * @param  {Array} learnDataSet    학습 데이터 배열
   * @param  {Date} startDatetime 시작 날짜
   * @param  {Date} endDatetime   종료 날짜
   * @return {Number}               설정한 기간 범위와 겹치는 학습 데이터의 index. 없을 경우 -1을 반환한다.
   */
  findOverlapLearnData(learnDataSet, startDatetime, endDatetime) {
    const startDate = removeTime(startDatetime);
    const endDate = removeTime(endDatetime);

    let duplicateIndex = -1;
    if (!isEmpty(learnDataSet)) {
      forEach(learnDataSet, (learnData, index) => {
        if ((startDate >= learnData.startDatetime && startDate <= learnData.endDatetime) || (endDate >= learnData.startDatetime && endDate <= learnData.endDatetime) || (startDate < learnData.startDatetime && endDate > learnData.endDatetime)) {
          duplicateIndex = index;
          learnData.duplicate = true;
        } else {
          learnData.duplicate = false;
        }
      });
    }
    return duplicateIndex;
  },
  /**
   * 학습 데이터의 중복 체크를 해제한다.
   * @author Ju
   * @param  {Array} learnDataSet    학습 데이터 배열
   * @return {Array}
   */
  clearDuplicate(learnDataSet) {
    if (!isEmpty(learnDataSet)) {
      forEach(learnDataSet, (learnData) => {
        learnData.duplicate = false;
      });
    }
    return learnDataSet;
  },
  /**
   * 최대 학습 일수 초과 여부를 체크한다.
   * 초과: true, 초과하지 않음: false
   * @author Ju
   * @param  {Array} learnDataSet    학습 데이터 배열
   * @param  {Date} startDatetime 시작 날짜
   * @param  {Date} endDatetime   종료 날짜
   * @param  {Number} maxNumberOfLearnDay 최대 학습 일수
   * @return {Boolean}
   */
  isExceedLearnDayCount(learnDataSet, startDatetime, endDatetime, maxNumberOfLearnDay) {
    const startDate = removeTime(startDatetime);
    const endDate = removeTime(endDatetime);
    const diff = moment(endDate).diff(startDate, 'd') + 1;
    let totalDayCount = 0;
    if (!isEmpty(learnDataSet)) {
      forEach(learnDataSet, (item) => {
        totalDayCount += item.learnDayCount;
      });
    }
    return maxNumberOfLearnDay < totalDayCount + diff;
  },
  /**
   * 시작/종료 기간에 대한 새로운 학습 데이터 객체를 생성하여 학습 데이터 배열에 추가하고 정렬 후 학습 데이터 배열을 반환한다.
   * @author Ju
   * @param  {Array} learnDataSet    학습 데이터 배열
   * @param  {Date} startDatetime 시작 날짜
   * @param  {Date} endDatetime   종료 날짜
   * @return {Array} 학습 데이터 배열
   */
  addLearnData(learnDataSet, startDatetime, endDatetime) {
    const startDate = removeTime(startDatetime);
    const endDate = removeTime(endDatetime);

    const learnDayCount = moment(endDatetime).diff(startDatetime, 'days') + 1;
    const label = learnDayCount > 1 ? `${moment(startDate).format(this.LABEL_DATE_FORMAT)} ~ ${moment(endDate).format(this.LABEL_DATE_FORMAT)} (${learnDayCount}일)` : `${moment(endDate).format(this.LABEL_DATE_FORMAT)} (${learnDayCount}일)`;

    learnDataSet.push({
      startDatetime: startDate,
      startDateLabel: moment(startDate).format('YYYY-MM-DD'),
      endDatetime: endDate,
      endDateLabel: moment(endDate).format('YYYY-MM-DD'),
      learnDayCount,
      label,
      duplicate: false,
    });

    return sortBy(learnDataSet, (item) => item.startDatetime);
  },
  /**
   * 학습 데이터 배열을 날짜 문자열 배열로 가공하여 반환한다.
   * @author Ju
   * @param  {Array} learnDataSet 학습 데이터 배열
   * @return {Array}            (ex) ['2019-04-25','2019-04-26']
   */
  convertLearnDataSetToDateStringArray(learnDataSet, registDatetime) {
    const dateFormat = 'YYYY-MM-DD';
    const dateArray = [];
    const registDate = registDatetime ? removeTime(registDatetime) : null;
    if (!isEmpty(learnDataSet)) {
      forEach(learnDataSet, (item) => {
        let startDatetime = item.startDatetime;
        const endDatetime = item.endDatetime;
        while (startDatetime <= endDatetime) {
          startDatetime = moment(startDatetime);
          if (registDate === null || startDatetime >= registDate) {
            dateArray.push(startDatetime.format(dateFormat));
          }
          startDatetime = startDatetime.add(1, 'd').toDate();
        }
      });
    }
    return dateArray;
  },
  /**
   * 학습 데이터 배열을 날짜 배열로 가공하여 반환한다.
   * @author Ju
   * @param  {Array} learnDataSet 학습 데이터 배열
   * @return {Array}            (ex) [2019-04-25,2019-04-26]
   */
  convertLearnDataSetToDateArray(learnDataSet) {
    const dateArray = [];
    if (!isEmpty(learnDataSet)) {
      forEach(learnDataSet, (item) => {
        let startDatetime = item.startDatetime;
        const endDatetime = item.endDatetime;
        while (startDatetime <= endDatetime) {
          dateArray.push(startDatetime);
          startDatetime = moment(startDatetime)
            .add(1, 'd')
            .toDate();
        }
      });
    }
    return dateArray;
  },
  /**
   * 학습 데이터 배열에 대한 라벨을 가공하여 반환한다.
   * @author Ju
   * @param  {Array} learnDataSet 학습 데이터 배열
   * @return {String}
   */
  getLearnDataSetLabel(learnDataSet) {
    let learnDataSetLabel = null;
    if (!isEmpty(learnDataSet)) {
      let totalDayCount = 0;
      const dayLabels = [];
      forEach(learnDataSet, (item) => {
        totalDayCount += item.learnDayCount;
        const label = item.learnDayCount > 1 ? `${moment(item.startDatetime).format(this.LABEL_DATE_FORMAT)} ~ ${moment(item.endDatetime).format(this.LABEL_DATE_FORMAT)}` : `${moment(item.startDatetime).format(this.LABEL_DATE_FORMAT)}`;
        dayLabels.push(label);
      });
      learnDataSetLabel = `${totalDayCount}일 (${dayLabels.join(', ')})`;
    }
    return learnDataSetLabel;
  },
  /**
   * 학습 데이터 배열 안의 학습 데이터의 총 학습 일수를 반환한다.
   * @author Ju
   * @param  {Array} learnDataSet 학습 데이터 배열
   * @return {Number}
   */
  getTotalLearnDayCount(learnDataSet) {
    let total = 0;
    if (!isEmpty(learnDataSet)) {
      forEach(learnDataSet, (item) => {
        total += item.learnDayCount;
      });
    }
    return total;
  },
  /**
   * 예측 기간에 맞는 예측 결과 시간 간격을 반환한다.
   * @author Ju
   * @param  {Number} predictPeriod              예측 기간
   * @param  {String} predictPeriodUnit           예측 기간 단위
   * @return {String}                  예측 결과 시간 간격
   */
  getPreditTimeInterval(predictPeriod, predictPeriodUnit) {
    let interval;
    const predictDays = this.convertToDays(predictPeriod, predictPeriodUnit);
    const endDatetime = moment();
    const startDatetime = endDatetime.subtract(predictDays, 'd');

    const type = getStatisticType(startDatetime.toDate(), endDatetime.toDate());
    if (type === statisticType.DAY) {
      interval = '5min';
    } else if (type === statisticType.WEEK) {
      interval = '30min';
    } else if (type === statisticType.MONTH) {
      interval = '2h';
    } else {
      interval = '1d';
    }
    return interval;
  },
};



// WEBPACK FOOTER //
// ./src/utils/predict-performance.js